import { z } from "zod";

import { ENTITY_KINDS, MAX_EVENTS_PER_BATCH } from "./constants.ts";

const shortText = z.string().trim().min(1).max(200);
const entityId = z.string().trim().min(1).max(100);

/** Where in the app the event happened. Paths only: query strings can carry personal data. */
export const pageRefSchema = z.object({
	path: z.string().startsWith("/").max(500),
	title: z.string().max(200).optional()
});

/**
 * The content the user is working in when the event happens: the Company › Project › Solution
 * path of the content architecture. The context service keeps the latest one as the user's focus.
 */
export const activityScopeSchema = z.object({
	companyId: entityId.optional(),
	projectId: entityId.optional(),
	solutionId: entityId.optional()
});

export type ActivityScope = z.infer<typeof activityScopeSchema>;

const eventBase = {
	id: z.uuid(),
	occurredAt: z.iso.datetime(),
	/** One browser tab session; resets when the tab closes. */
	sessionId: z.uuid(),
	page: pageRefSchema,
	scope: activityScopeSchema.optional()
};

const requirementValue = z.union([z.string().max(200), z.number(), z.boolean(), z.array(z.string().max(100)).max(20)]);

/**
 * Every activity event the web app can emit, discriminated on `type`. Adding a type here is the
 * whole change on the producer side: the API validates against this union and the context
 * service's reducer is exhaustive over it, so the compiler points at every place that must learn
 * the new type.
 */
export const activityEventSchema = z.discriminatedUnion("type", [
	z.object({ ...eventBase, type: z.literal("page.viewed"), data: z.object({}) }),
	z.object({
		...eventBase,
		type: z.literal("entity.viewed"),
		data: z.object({ kind: z.enum(ENTITY_KINDS), id: entityId, name: shortText.optional() })
	}),
	z.object({
		...eventBase,
		type: z.literal("search.performed"),
		data: z.object({ query: shortText, resultCount: z.number().int().nonnegative().optional() })
	}),
	z.object({
		...eventBase,
		type: z.literal("filter.changed"),
		data: z.object({ filters: z.record(z.string().max(50), requirementValue) })
	}),
	z.object({
		...eventBase,
		type: z.literal("requirement.updated"),
		data: z.object({ key: z.string().trim().min(1).max(50), value: requirementValue })
	}),
	z.object({
		...eventBase,
		type: z.literal("document.opened"),
		data: z.object({ documentId: entityId, title: shortText.optional() })
	}),
	z.object({
		...eventBase,
		type: z.literal("cta.clicked"),
		data: z.object({ ctaId: z.string().trim().min(1).max(100), label: shortText.optional() })
	}),
	z.object({ ...eventBase, type: z.literal("advisor.opened"), data: z.object({}) }),
	z.object({
		...eventBase,
		type: z.literal("advisor.messageSent"),
		// The message text itself goes to the AI infra through the chat route, not through here.
		data: z.object({ conversationId: z.string().min(1).max(200) })
	})
]);

export type ActivityEvent = z.infer<typeof activityEventSchema>;
export type ActivityEventType = ActivityEvent["type"];
export type ActivityEventOf<TType extends ActivityEventType> = Extract<ActivityEvent, { type: TType }>;

/** What the browser posts to `POST /api/activity`. */
export const activityBatchSchema = z.object({
	events: z.array(activityEventSchema).min(1).max(MAX_EVENTS_PER_BATCH)
});

export type ActivityBatch = z.infer<typeof activityBatchSchema>;
